import { useCallback, useMemo, useState } from "react";
import Spinner from "../../common/components/Spinner/Spinner";
import { NOTIFICATIONS_SETTINGS_PAGE } from "../../utils/url/staticUrls";
import Spacer from "../../common/components/Spacer";
import { NotificationsFiltersModal } from "./components/NotificationsFiltersModal";
import { IMG_EMPTY_NOTIFICATIONS } from "../../utils/url/staticResources";
import Pagination from "../../Search/components/common/Pagination";
import useMediaQuery from "../../common/hooks/useMediaQuery";
import { useBooleanState } from "../../common/hooks/useBooleanState";
import { useOnMount } from "../../common/hooks/useOnMount";
import {
  deleteNotificationsThunk,
  initialiseNotificationsPageThunk,
  markNotificationsAsReadThunk,
} from "../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsNotificationsError,
  selectIsNotificationsLoaded,
  selectIsNotificationsLoading,
  selectNotifications,
} from "../redux/NotificationsSelectors";
import { MEDIA_QUERY_MIN_TABLET } from "../../utils/screenSizes";
import {
  fetchNavigationFilters,
  type NotificationFilterEntry,
  NotificationsFilters,
  useNotificationFiltering,
  useNotificationFilters,
} from "~/Notifications/NotificationsPage/components/NotificationsFilters";

import classes from "./NotificationsPage.module.less";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { Link, LinkButton } from "~/common/components/Link";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { NotificationsList } from "~/Notifications/NotificationsPage/components/NotificationsList";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";

const PAGE_SIZE = 10;

const getPageAmount = (elementCount: number) => Math.ceil(elementCount / PAGE_SIZE);

export function NotificationsPage() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isMinTable = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const { val: isModalOpened, setFalse: closeModal, setTrue: openModal } = useBooleanState(false);

  const notifications = useSelector(selectNotifications);
  const isLoading = useSelector(selectIsNotificationsLoading);
  const isLoaded = useSelector(selectIsNotificationsLoaded);
  const isError = useSelector(selectIsNotificationsError);

  const [page, setPage] = useState(0);

  const disableInteractions = !isLoaded;

  const markNotificationsAsRead = useCallback(
    (uids: string | string[], isRead: boolean) => dispatch(markNotificationsAsReadThunk(uids, isRead)),
    [dispatch],
  );

  useOnMount(() => dispatch(initialiseNotificationsPageThunk()));

  const { filters, changeFilter, replaceFilters } = useNotificationFilters(fetchNavigationFilters);

  const onFilterChange: typeof changeFilter = (filter) => {
    changeFilter(filter);
    setPage(0);
  };

  const filteredNotifications = useNotificationFiltering(filters, notifications);

  const numberOfPages = getPageAmount(filteredNotifications.length);
  const visiblePage = useMemo(
    () => filteredNotifications.slice(PAGE_SIZE * page, PAGE_SIZE * (page + 1)),
    [filteredNotifications, page],
  );

  const deleteNotifications = useCallback(
    (uids: string | string[]) => {
      uids = Array.isArray(uids) ? uids : [uids];
      dispatch(deleteNotificationsThunk(uids));
      const remainingAmount = filteredNotifications.length - uids.length;
      const remainingPages = getPageAmount(remainingAmount);
      if (remainingPages < numberOfPages && page === numberOfPages - 1) {
        setPage((curPage) => curPage - 1);
      }
    },
    [dispatch, page, filteredNotifications],
  );

  const onApplyNewFilters = (newFilters: NotificationFilterEntry[]) => {
    closeModal();
    replaceFilters(newFilters);
  };

  return (
    <>
      <div className={classes.page}>
        {isMinTable && (
          <div className={classes.filtersSlot}>
            <NotificationsFilters disabled={disableInteractions} filters={filters} onFilterChange={onFilterChange} />
          </div>
        )}

        <div className={classes.notificationsSlot}>
          <div className={classes.pageTitleContainer}>
            <h1 className={"scha__page-title"}>
              {t("notificationsPage.title", { count: filteredNotifications.length })}
            </h1>
            <Stacker gap={16} direction={"row"}>
              <Link
                iconBefore={<Icon type={IconType.Cog} />}
                href={NOTIFICATIONS_SETTINGS_PAGE}
                data-ta-spec={"notification-settings"}
              >
                {t("notificationsPage.settings.title")}
              </Link>
              {!isMinTable && (
                <LinkButton
                  onClick={openModal}
                  disabled={disableInteractions}
                  iconBefore={<Icon type={IconType.Filter} />}
                >
                  {t("notificationsPage.settings.filter")}
                </LinkButton>
              )}
            </Stacker>
          </div>
          <Spacer className={"scha__page-title__spacer"} />

          {isLoading && <Spinner className="mb-3" />}

          {isLoaded && visiblePage.length > 0 && (
            <NotificationsList
              data={visiblePage}
              onDeleteItems={deleteNotifications}
              onMarkAsRead={markNotificationsAsRead}
            />
          )}

          {numberOfPages > 1 && (
            <Pagination
              classes={{ root: "scha__srp__pagination" }}
              pageAmount={numberOfPages}
              currentPage={page}
              onPageChange={setPage}
              goToIdAfterChange={"#resultsHeader"}
            />
          )}

          {isError && <Alert severity="error">{t("notificationsPage.error")}</Alert>}

          {isLoaded && !visiblePage.length && (
            <div className={classes.noResultsContainer}>
              <img className={classes.noResultsImage} src={IMG_EMPTY_NOTIFICATIONS} alt="no notifications" />
              <h2>{t("notification.noResults.title")}</h2>
              <p>{t("notification.noResults.description")}</p>
            </div>
          )}
        </div>
      </div>
      {isModalOpened && (
        <NotificationsFiltersModal currentFilters={filters} onClose={closeModal} onApply={onApplyNewFilters} />
      )}
    </>
  );
}
